import React, { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import { 
  Users, Heart, Zap, Shield, Globe, Award, 
  Building2, Briefcase, GraduationCap, Coffee,
  Laptop, Clock, Sun, Leaf, Smile, Filter
} from 'lucide-react';
import toast from 'react-hot-toast';
import JobCard from '../components/careers/JobCard';
import JobFilters from '../components/careers/JobFilters';
import type { Job, Value, Benefit } from '../types/careers';

const Careers: React.FC = () => {
  useEffect(() => {
    document.title = 'Carrières | Proxy Emobility';
  }, []);

  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('Tous les départements');
  const [selectedLocation, setSelectedLocation] = useState('Tous les lieux');
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);

  const values: Value[] = [
    {
      icon: <Heart className="w-12 h-12 text-primary-500" />,
      title: "Passion",
      description: "Nous sommes passionnés par l'innovation et la mobilité durable."
    },
    {
      icon: <Users className="w-12 h-12 text-primary-500" />,
      title: "Collaboration",
      description: "Le travail d'équipe est au cœur de notre réussite."
    },
    {
      icon: <Zap className="w-12 h-12 text-primary-500" />,
      title: "Innovation",
      description: "Nous repoussons constamment les limites de la technologie."
    },
    {
      icon: <Shield className="w-12 h-12 text-primary-500" />,
      title: "Intégrité",
      description: "L'honnêteté et la transparence guident nos actions."
    }
  ];

  const benefits: Benefit[] = [
    {
      icon: <GraduationCap className="w-8 h-8 text-primary-500" />,
      title: "Formation Continue",
      description: "Budget formation annuel et accès à des ressources d'apprentissage."
    },
    {
      icon: <Coffee className="w-8 h-8 text-primary-500" />,
      title: "Environnement Stimulant",
      description: "Espaces de travail modernes et collaboratifs."
    },
    {
      icon: <Laptop className="w-8 h-8 text-primary-500" />,
      title: "Flexibilité",
      description: "Possibilité de télétravail et horaires flexibles."
    },
    {
      icon: <Clock className="w-8 h-8 text-primary-500" />,
      title: "Équilibre Vie Pro/Perso",
      description: "Respect de votre temps personnel et de vos engagements."
    },
    {
      icon: <Sun className="w-8 h-8 text-primary-500" />,
      title: "Bien-être",
      description: "Programme de bien-être et activités team building."
    },
    {
      icon: <Leaf className="w-8 h-8 text-primary-500" />,
      title: "Impact Positif",
      description: "Contribuez à un avenir plus durable."
    }
  ];

  const jobs: Job[] = [
    {
      id: '1',
      title: 'Ingénieur Développement Full Stack',
      department: 'Technologie',
      location: 'Dakar, Sénégal',
      type: 'full-time',
      experience: '3-5 ans',
      description: 'Nous recherchons un ingénieur full stack passionné pour rejoindre notre équipe de développement et contribuer à la création de solutions innovantes pour la mobilité électrique.',
      requirements: [
        'Expérience en développement web avec React et Node.js',
        'Maîtrise des bases de données SQL et NoSQL',
        'Connaissance des principes de CI/CD',
        'Expérience avec les architectures cloud',
        'Excellentes capacités de communication'
      ],
      responsibilities: [
        'Développer et maintenir nos applications web et mobiles',
        'Collaborer avec l\'équipe produit pour concevoir de nouvelles fonctionnalités',
        'Optimiser les performances des applications',
        'Participer aux revues de code et au mentorat',
        'Contribuer à l\'amélioration continue de nos processus'
      ],
      benefits: [
        'Package salarial compétitif',
        'Stock options',
        'Assurance santé',
        'Formation continue',
        'Environnement de travail flexible'
      ]
    },
    {
      id: '2',
      title: 'Chef de Projet Innovation',
      department: 'Innovation',
      location: 'Abidjan, Côte d\'Ivoire',
      type: 'remote',
      experience: '5-7 ans',
      description: 'Dirigez des projets d\'innovation stratégiques pour développer nos solutions de mobilité électrique de nouvelle génération.',
      requirements: [
        'Expérience en gestion de projets technologiques',
        'Connaissance du secteur de la mobilité électrique',
        'Leadership et capacité à motiver une équipe',
        'Excellentes compétences analytiques',
        'Maîtrise de l\'anglais et du français'
      ],
      responsibilities: [
        'Définir et piloter la stratégie d\'innovation',
        'Gérer une équipe pluridisciplinaire',
        'Collaborer avec les parties prenantes',
        'Suivre les KPIs et le budget',
        'Présenter les résultats à la direction'
      ],
      benefits: [
        'Rémunération attractive',
        'Bonus annuel',
        'Plan d\'épargne entreprise',
        'Formation internationale',
        'Télétravail flexible'
      ]
    },
    {
      id: '3',
      title: 'Responsable Marketing Digital',
      department: 'Marketing',
      location: 'Casablanca, Maroc',
      type: 'full-time',
      experience: '3-5 ans',
      description: 'Développez et mettez en œuvre notre stratégie de marketing digital pour accroître notre présence en ligne et générer des leads qualifiés.',
      requirements: [
        'Expérience en marketing digital B2B',
        'Maîtrise des outils d\'analyse web',
        'Compétences en création de contenu',
        'Connaissance des réseaux sociaux',
        'Capacité à gérer plusieurs projets'
      ],
      responsibilities: [
        'Élaborer la stratégie marketing digital',
        'Gérer les campagnes publicitaires',
        'Créer du contenu engageant',
        'Analyser les performances',
        'Optimiser le ROI marketing'
      ],
      benefits: [
        'Salaire compétitif',
        'Prime sur objectifs',
        'Formation continue',
        'Événements d\'équipe',
        'Horaires flexibles'
      ]
    }
  ];

  const departments = ['Tous les départements', ...new Set(jobs.map(job => job.department))];
  const locations = ['Tous les lieux', ...new Set(jobs.map(job => job.location))];

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment = selectedDepartment === 'Tous les départements' || job.department === selectedDepartment;
    const matchesLocation = selectedLocation === 'Tous les lieux' || job.location === selectedLocation;
    return matchesSearch && matchesDepartment && matchesLocation;
  });

  const handleDownloadPDF = (job: Job) => {
    const doc = new jsPDF();
    
    // Add content to PDF
    doc.setFontSize(20);
    doc.text(job.title, 20, 20);
    
    doc.setFontSize(12);
    doc.text(`Département: ${job.department}`, 20, 40);
    doc.text(`Localisation: ${job.location}`, 20, 50);
    doc.text(`Type: ${job.type}`, 20, 60);
    doc.text(`Expérience: ${job.experience}`, 20, 70);
    
    doc.text('Description:', 20, 90);
    doc.text(job.description, 20, 100);
    
    // Save the PDF
    doc.save(`${job.title.toLowerCase().replace(/ /g, '-')}.pdf`);
    
    toast.success('Description du poste téléchargée !');
  };

  const handleApply = (job: Job) => {
    setSelectedJob(job);
    setShowApplicationForm(true);
    toast.success('Formulaire de candidature ouvert');
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <section className="bg-primary-900 text-white py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Rejoignez l'Aventure
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Contribuez à la révolution de la mobilité électrique en Afrique. Nous recherchons des talents passionnés pour construire un avenir plus durable.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-50 rounded-full mb-4">
                <Building2 className="w-8 h-8 text-primary-500" />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">3</div>
              <div className="text-gray-600">Bureaux</div>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-50 rounded-full mb-4">
                <Users className="w-8 h-8 text-primary-500" />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">50+</div>
              <div className="text-gray-600">Employés</div>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-50 rounded-full mb-4">
                <Globe className="w-8 h-8 text-primary-500" />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">10</div>
              <div className="text-gray-600">Pays</div>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-50 rounded-full mb-4">
                <Award className="w-8 h-8 text-primary-500" />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">4.8/5</div>
              <div className="text-gray-600">Note Employés</div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Nos Valeurs
            </h2>
            <p className="text-lg text-gray-600">
              Ces valeurs fondamentales guident nos actions et définissent notre culture d'entreprise.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg text-center hover:shadow-xl transition-shadow">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-50 rounded-full mb-6">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Avantages
            </h2>
            <p className="text-lg text-gray-600">
              Nous offrons un environnement de travail stimulant et des avantages compétitifs pour votre épanouissement.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition-colors">
                <div className="flex items-start">
                  <div className="p-2 bg-white rounded-lg shadow-sm mr-4">
                    {benefit.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Jobs Section */}
      <section className="py-16 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Postes Ouverts
            </h2>
            <p className="text-lg text-gray-600">
              Découvrez nos opportunités actuelles et rejoignez une équipe dynamique et passionnée.
            </p>
          </div>

          <JobFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            selectedDepartment={selectedDepartment}
            onDepartmentChange={setSelectedDepartment}
            selectedLocation={selectedLocation}
            onLocationChange={setSelectedLocation}
            departments={departments}
            locations={locations}
          />

          <div className="mt-8 space-y-6">
            {filteredJobs.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl shadow-sm">
                <Filter size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucun poste trouvé</h3>
                <p className="text-gray-600">
                  Essayez de modifier vos critères de recherche
                </p>
              </div>
            ) : (
              filteredJobs.map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  isExpanded={expandedJobId === job.id}
                  onToggle={() => setExpandedJobId(expandedJobId === job.id ? null : job.id)}
                  onDownload={handleDownloadPDF}
                  onApply={handleApply}
                />
              ))
            )}
          </div>
        </div>
      </section>

      {/* Culture Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Notre Culture
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Chez Proxy Emobility, nous cultivons un environnement de travail inclusif et dynamique où chaque membre de l'équipe peut s'épanouir et contribuer à notre mission commune.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="p-2 bg-primary-50 rounded-lg mr-4">
                    <Smile className="w-6 h-6 text-primary-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Bien-être au travail</h3>
                    <p className="text-gray-600">Un environnement positif et stimulant</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="p-2 bg-primary-50 rounded-lg mr-4">
                    <Users className="w-6 h-6 text-primary-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Esprit d'équipe</h3>
                    <p className="text-gray-600">Collaboration et entraide au quotidien</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="p-2 bg-primary-50 rounded-lg mr-4">
                    <Briefcase className="w-6 h-6 text-primary-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Évolution de carrière</h3>
                    <p className="text-gray-600">Des opportunités de croissance</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg"
                alt="Culture d'entreprise 1"
                className="rounded-lg shadow-lg"
              />
              <img
                src="https://images.pexels.com/photos/3184296/pexels-photo-3184296.jpeg"
                alt="Culture d'entreprise 2"
                className="rounded-lg shadow-lg mt-8"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;